// Only speculative responses are retained. A normal read consumes the entry,
// so polling and explicit refreshes still reach the server after navigation.
export function createPrefetchCache(identity, { ttl = 20000, maxEntries = 32, now = Date.now } = {}) {
  const entries = new Map();
  let scope = identity();
  const syncScope = () => {
    const next = identity();
    if (scope !== next) { entries.clear(); scope = next; }
  };
  const entryFor = key => {
    syncScope();
    const entry = entries.get(key);
    if (entry && now() - entry.time >= ttl) { entries.delete(key); return undefined; }
    return entry;
  };
  return {
    invalidate() { entries.clear(); },
    remove(key) { entries.delete(key); },
    peek(key) { return entryFor(key)?.value; },
    prefetch(key, load) {
      const cached = entryFor(key);
      if (cached) return cached.promise;
      if (entries.size >= maxEntries) entries.delete(entries.keys().next().value);
      const entry = { time: now() };
      entry.promise = Promise.resolve().then(load).then(value => {
        entry.value = value;
        return value;
      }).catch(error => {
        if (entries.get(key) === entry) entries.delete(key);
        throw error;
      });
      entries.set(key, entry);
      return entry.promise;
    },
    read(key, load) {
      const cached = entryFor(key);
      if (!cached) return load();
      entries.delete(key);
      // A failed speculative request must not prevent normal navigation retry.
      return cached.promise.catch(() => load());
    },
  };
}
